import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Autocomplete, AutocompleteProps, Typography } from "@mui/material";
import { FastField, Field, FieldInputProps, FieldMetaProps } from "formik";
import React from "react";

import { palette } from "~frontend/theme";

import { Checkbox } from "../Checkbox";
import FormTextField from "./FormTextField";

export type AutocompleteValue = {
  id: string;
  label: string;
};

type FormInMemoryComboboxProps = {
  label: string;
  error: boolean;
  helperText: React.ReactNode;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
} & Omit<
  AutocompleteProps<AutocompleteValue, true, false, false>,
  "renderInput"
>;

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = (
  <CheckBox fontSize="small" sx={{ color: palette.darkGreen }} />
);

export function FormInMemoryCombobox(
  props: FormInMemoryComboboxProps
): React.ReactElement {
  return (
    <Autocomplete
      disableCloseOnSelect
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Typography
            variant="body1"
            component="span"
            {...getTagProps({ index })}
            key={`auto-complete-value-${index}-${option.id}`}
          >
            {option.label}
            {index < value.length - 1 ? "," : ""}
          </Typography>
        ))
      }
      multiple
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      ListboxProps={{ style: { maxHeight: "250px" } }}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: "preventOverflow",
              enabled: true,
            },
          ],
        },
      }}
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          style={
            selected
              ? {
                  backgroundColor: palette.lightGray,
                  boxShadow: "0px 0px 0px 2px white inset",
                }
              : undefined
          }
        >
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />

          <Typography variant="body2">{option.label}</Typography>
        </li>
      )}
      renderInput={params => (
        <FormTextField
          {...params}
          error={props.error}
          helperText={props.helperText}
          InputProps={{
            ...(params.InputProps || {}),
            startAdornment: (
              <>
                {props.startAdornment}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {props.endAdornment}
              </>
            ),
          }}
          variant="outlined"
          label={props.label}
        />
      )}
      {...props}
    />
  );
}

export function FormikInMemoryCombobox({
  name,
  shouldUpdate,
  fast,
  overrideFormikProps,
  bottomLabel,
  ...childProps
}: Omit<
  FormInMemoryComboboxProps,
  "value" | "onChange" | "renderInput" | "error" | "helperText"
> & {
  name: string;
  shouldUpdate?: (props: any, prevProps: any) => boolean;
  fast?: boolean;
  overrideFormikProps?: (
    field: FieldInputProps<string[]>,
    meta: FieldMetaProps<string[]>
  ) => FieldInputProps<string[]>;
  bottomLabel?: string;
}): React.ReactElement {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name} shouldUpdate={shouldUpdate}>
      {({
        field,
        meta,
        form,
      }: {
        field: FieldInputProps<string[]>;
        meta: FieldMetaProps<string[]>;
        form: any;
      }) => (
        <FormInMemoryCombobox
          {...(overrideFormikProps ? overrideFormikProps(field, meta) : field)}
          {...childProps}
          multiple
          value={childProps.options.filter(({ id }) =>
            field.value.includes(id)
          )}
          onChange={(_event, options) =>
            form.setFieldValue(
              name,
              options.map(o => o.id),
              true
            )
          }
          error={meta.touched && Boolean(meta.error)}
          helperText={
            meta.touched && Boolean(meta.error) ? meta.error : bottomLabel
          }
        />
      )}
    </Component>
  );
}
